import type { PropsWithChildren, FunctionComponent } from 'react'
import { useMemo } from 'react'
import { matchPath, useLocation } from 'react-router'
import { useCurrentAccountDetails } from '../../../../api/current-account/currentAccount.hooks'
import { CurrentAccountContext } from '../../../../utils/current-account/CurrentAccountContext'
import {
  StoreConfigurationRelationshipEnum,
  useGetRetailersForPartnerQuery,
  useGetRetailerLocalesMeshQuery,
  useGetAllRetailerLocationsForRetailerQuery,
  useGetRetailerQuery,
  useCheckLaunchOnboardingProgressByPartnerQuery,
} from '../../../../__codegen__/api'
import { URL_PARTNER_SCOPED, URL_RETAILER_SCOPED } from '../../../../utils/routing/routes'
import { useStoreConfigurationsByPartnerId } from '../../../../api/store-configurations/useStoreConfigurations.hooks'

/**
 * Triggers Partner Data fetches in parallel with the main Auth check.
 * Renders as a sibling to children so query state updates don't cause children to re-render.
 *
 * Apollo will cache the results, and subsequent requests from components lower in the tree
 * will receive cached data.
 */
const DataPrefetcher = (): null => {
  const { pathname } = useLocation()

  // Manually check URL for partner_id since we are above the actual Router Routes
  const partnerMatch = matchPath<{ partner_id: string }>(pathname, {
    path: URL_PARTNER_SCOPED,
  })
  const partnerIdFromUrl = partnerMatch?.params.partner_id

  // Also check for retailer_id (warehouse_id) in URL to prefetch retailer data early
  const retailerMatch = matchPath<{ partner_id: string; warehouse_id: string }>(pathname, {
    path: URL_RETAILER_SCOPED,
  })
  const retailerIdFromUrl = retailerMatch?.params.warehouse_id

  useGetRetailersForPartnerQuery({
    variables: { partnerId: partnerIdFromUrl },
    skip: !partnerIdFromUrl,
  })

  useStoreConfigurationsByPartnerId({
    partnerId: partnerIdFromUrl,
    storeConfigRelationship: StoreConfigurationRelationshipEnum.Associated,
  })

  useCheckLaunchOnboardingProgressByPartnerQuery({
    variables: { partnerId: partnerIdFromUrl! },
    skip: !partnerIdFromUrl,
  })

  // // Prefetch retailer data if we're navigating to a retailer-scoped route
  // // This allows WarehouseProvider to receive cached data immediately
  // const shouldPrefetchRetailer =
  //   retailerIdFromUrl && retailerIdFromUrl !== '0' && retailerIdFromUrl !== 'undefined'

  // // Prefetch retailer data using raw Apollo hooks to populate Apollo's in-memory cache.
  // // WarehouseProvider will then get instant cache hits and write to IndexedDB.
  // useGetRetailerQuery({
  //   variables: { retailerId: retailerIdFromUrl! },
  //   skip: !shouldPrefetchRetailer,
  // })

  // useGetRetailerLocalesMeshQuery({
  //   variables: { retailerId: retailerIdFromUrl! },
  //   skip: !shouldPrefetchRetailer,
  // })

  // useGetAllRetailerLocationsForRetailerQuery({
  //   variables: { retailerId: retailerIdFromUrl },
  //   skip: !shouldPrefetchRetailer,
  // })

  return null
}

/**
 * Preloads globally-required data (account details, partner data) and provides
 * the current account context to the entire app tree.
 *
 * This component is placed high in the tree (in RPPCore) so that account fetch
 * starts immediately while domains are still loading in parallel.
 *
 * DataPrefetcher is rendered as a sibling to children to prevent query state
 * updates from causing unnecessary re-renders of the children tree.
 *
 * Note: Domain registration is handled separately by LazyContextProvider.
 * This component only waits for account details.
 */
export const AppProviderPreloader: FunctionComponent<PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { loading, authorized, actionRequired, account, refetch } = useCurrentAccountDetails()

  const contextValue = useMemo(
    () => ({
      account,
      refetch,
      authorized,
      actionRequired,
    }),
    [account, refetch, authorized, actionRequired]
  )

  return (
    <>
      <DataPrefetcher />
      {loading ? null : (
        <CurrentAccountContext.Provider value={contextValue}>
          {children}
        </CurrentAccountContext.Provider>
      )}
    </>
  )
}
